import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User } from '../types/api.types';
import { apiService } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, user: User) => void;
  logout: () => Promise<void>;
  setUser: (user: User) => void;
  switchOrganization: (orgCode: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Check for existing session on mount
  // If localStorage token is invalid/missing, try to restore from SSO cookie
  useEffect(() => {
    const checkAuth = async () => {
      const token = apiService.getToken();
      if (token) {
        try {
          const userData = await apiService.getMe();
          setUserState(userData);
          setIsLoading(false);
          return;
        } catch {
          apiService.clearToken();
          // Token was invalid - fall through to try SSO cookie
        }
      }

      // No valid localStorage token - try to restore session from SSO cookie
      // This handles the case where user logged out but SSO cookie is still valid
      // (e.g., navigating to settings from public-site after re-login)
      try {
        const currentPath = window.location.pathname;
        // Only try SSO restore if we're on a protected page (not login)
        if (currentPath !== '/login') {
          const sessionResponse = await apiService.getSessionFromCookie();
          if (sessionResponse.access_token) {
            apiService.setToken(sessionResponse.access_token);
            const userData = await apiService.getMe();
            setUserState(userData);
            setIsLoading(false);
            return;
          }
        }
      } catch {
        // SSO cookie invalid or missing - user needs to login
      }

      setIsLoading(false);
    };
    checkAuth();
  }, []);

  // Token refresh timer
  useEffect(() => {
    if (!isAuthenticated) return;

    // Refresh token every 15 minutes
    const refreshInterval = setInterval(async () => {
      try {
        await apiService.refreshToken();
      } catch {
        // Token refresh failed, logout
        apiService.clearToken();
        setUserState(null);
      }
    }, 15 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  const login = useCallback((token: string, userData: User) => {
    apiService.setToken(token);
    setUserState(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch {
      // Ignore logout errors
    } finally {
      apiService.clearToken();
      setUserState(null);
    }
  }, []);

  const setUser = useCallback((userData: User) => {
    setUserState(userData);
  }, []);

  const switchOrganization = useCallback(async (orgCode: string) => {
    const response = await apiService.switchOrganization(orgCode);
    apiService.setToken(response.access_token);
    setUserState(response.user);
  }, []);

  const refreshUser = useCallback(async () => {
    const userData = await apiService.getMe();
    setUserState(userData);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        setUser,
        switchOrganization,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
